// @ts-nocheck
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Auth } from 'aws-amplify';
import ErrorCodes from '../../../services/ErrorCodes';
import i18n from '../../../translations';

const getAccessToken = async () =>
  (await Auth.currentSession()).getAccessToken().getJwtToken();

const apiUrl = `${process.env.REACT_APP_API_URL}`;

export const Api = {
  getAuthUser: async () => {
    return new RequestBuilder('/users/me').authenticated().execute();
  },
  updateUser: async (params: Record<string, any>) => {
    return new RequestBuilder('/users/me', 'PATCH')
      .body(params)
      .authenticated()
      .execute();
  },
  createBooking: async (params: Record<string, any>) => {
    return new RequestBuilder('/bookings/create-booking', 'POST')
      .body(params)
      .authenticated()
      .execute();
  },
  getMyBookings: async () => {
    return new RequestBuilder('/bookings/my-bookings')
      .authenticated()
      .execute();
  },
  cancelBooking: async (params: Record<string, any>) => {
    return new RequestBuilder('/bookings/cancel-booking', 'POST')
      .body(params)
      .authenticated()
      .execute();
  },
  getUpcomingEvents: async () => {
    return new RequestBuilder('/offerings').authenticated().execute();
  },
  requestCreditApi: async (params: Record<string, any>) => {
    return new RequestBuilder('/users/me/request-credits', 'POST')
      .body(params)
      .authenticated()
      .execute();
  },
  getRecommendedMentors: async () => {
    return new RequestBuilder('/recommendation/mentors')
      .authenticated()
      .execute();
  },
  getBookingHistory: async () => {
    return new RequestBuilder('/bookings/me').authenticated().execute();
  },
  getActiveMentors: async () => {
    return new RequestBuilder('/mentors').authenticated().execute();
  },
  getRecommendations: async () => {
    return new RequestBuilder('/recommendation').authenticated().execute();
  },
  getMentorDetails: async () => {
    return new RequestBuilder(`/cms/proxy/${i18n.language}/mentors`)
      .authenticated()
      .execute();
  },
  getTopics: async () => {
    return new RequestBuilder('/topics').authenticated().execute();
  },
  skipAssessment: async () => {
    return new RequestBuilder('/assessment/skip', 'POST')
      .authenticated()
      .execute();
  },
  audioPlayed: async (params: Record<string, any>) => {
    return new RequestBuilder('/analytics/audio-played', 'POST')
      .body(params)
      .authenticated()
      .execute();
  },
};

export function parseRes(res: {
  text?: any;
  ok?: any;
  status?: any;
  statusText?: any;
  url?: any;
}) {
  return res.text().then((text: string) => {
    let body;
    try {
      const parsed = JSON.parse(text);

      body = parsed.data || parsed.mentors;
    } catch (err) {
      body = text;
    }
    if (res.ok) {
      return body;
    }
  });
}

function parseError(err: { status: any; body: any }) {
  try {
    const { status, body } = err;
    const { errors: [{ code }] = [] } = body;
    const message = ErrorCodes[code] || i18n.t('UseApi.UnexpectedError');
    return { code, status, message };
  } catch (err) {
    return {
      code: null,
      status: 400,
      message: i18n.t('UseApi.UnexpectedError'),
    };
  }
}

class RequestBuilder {
  private readonly _request: RequestInit;

  private readonly _route: string;

  private _authenticated: boolean;

  constructor(route: string, method: string) {
    this._request = {
      method,
      headers: {
        'Content-Type': 'application/json',
      },
    };
    this._route = route;
    this._authenticated = false;
  }

  get(): RequestBuilder {
    this._user.method = 'GET';
    return this;
  }

  post(): RequestBuilder {
    this._request.method = 'POST';
    this._user.method = 'POST';
    return this;
  }

  authenticated(): RequestBuilder {
    this._authenticated = true;
    return this;
  }

  body(body: any): RequestBuilder {
    if (body && Object.keys(body).length) {
      this._request.body = JSON.stringify(body);
    }

    return this;
  }

  execute() {
    return getAccessToken()
      .then((token) => {
        this._request.headers.Authorization = `Bearer ${token}`;
      })
      .then(() => fetch(`${apiUrl}${this._route}`, this._request));
  }
}

export const apiRequest = <T>(name: string, params?: any): T => {
  return Api[name](params)
    .then(parseRes)
    .catch((e) => {
      console.error(e);
    });
};

export default <T>(name: string | number) => {
  const { push } = useHistory();
  const apiMethod = useCallback(
    (params: any = {}) => {
      return Api[name](params)
        .then(parseRes)
        .then(
          (res: T) => res,
          (err: { status: any; body?: {} | undefined }) => {
            const { status } = err;
            if (status === 401) {
              return push('/logout');
            }
            throw parseError(err);
          },
        );
    },
    [name, push],
  );

  return [apiMethod];
};
